/**
 * Glazed nails — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const glazedNailsPack: ToilePack = {
    slug: 'glazed-nails',
    category: 'beauty',
    name: 'Glazed nails',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pearl', label: 'Pearl glaze', subject: 'short almond nails in sheer milky white with a pearly white chrome glow'},
        {key: 'pink', label: 'Strawberry glaze', subject: 'oval nails in sheer pink with a soft pearl chrome over it'},
        {key: 'chocolate', label: 'Chocolate glaze', subject: 'square nails in a deep brown with a warm golden chrome sheen'},
    ],
    styles: [
        {key: 'marble', label: 'Marble', style: 'on white marble in soft diffuse daylight from camera-left, a bright clean balance so the pearly sheen shifts across each nail', palette: 'White and grey marble, the glaze the only colour', framing: 'The hand resting flat, seen from above, fingertips toward the top of the frame'},
        {key: 'coffee', label: 'Coffee date', style: 'holding a cream ceramic coffee cup at a café table in warm window light from camera-right, a warm mid-tone balance', palette: 'Cream ceramic, warm wood and coffee browns around the hand', framing: 'A near-frontal three-quarter view at table height, the café softly blurred'},
        {key: 'doughnut', label: 'Doughnut', style: 'the fingers resting beside a glazed doughnut on a pale pink plate in bright soft daylight from above, a high playful balance echoing the sheen of the nails', palette: 'Pale pink plate, sugar glaze and warm skin', framing: 'Squared from directly above, hand and doughnut side by side with an even margin'},
    ],
    directions: ['blue glaze', 'french tip', 'short nails'],
    directionHint: 'try: blue glaze',
    seo: {
        title: 'Glazed Nails: Pearl, Strawberry and Chocolate Glaze',
        metaDescription: 'Nine glazed nail designs: a pearl chrome glow, a strawberry pink glaze and a chocolate glaze with golden sheen, in three settings.',
        h1: 'Glazed nails: pearl glaze, strawberry glaze and chocolate glaze',
        tagline: 'Nine glazed sets: three glazes, each shown on white marble, with a coffee date and beside a doughnut.',
        intro: 'Glazed nails for people who want a sheer base with a pearly chrome rubbed over it, so the nail looks like a glazed doughnut. The three designs are short almond nails in milky white with a pearl chrome glow, oval sheer pink nails with a soft pearl chrome, and square deep brown nails with a warm golden sheen. Type a different glaze color and the same sheen carries it.',
        useCases: [
            'a pearl glaze for a wedding',
            'a pink glaze for spring',
            'a chocolate glaze for autumn',
            'a glazed look for a sweet-themed party',
        ],
        faq: [
            {q: 'What is a glazed nail?', a: 'A sheer base with a pearly or golden chrome rubbed over it, so the nail has a glossy sheen.'},
            {q: 'Is glaze a color or a finish?', a: 'A finish. The sheen shows over white, pink and brown bases.'},
            {q: 'Can I ask for a blue glaze?', a: 'Yes. A glaze color you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
